/**
 * BL-75 (D-210) · Touch-first placement — the armed-placeable provider.
 *
 * Builds a {@link PlaceableDescriptor} from the EXISTING source of truth —
 * `gs.placementMode === "firewall"` and `gs.selectedTowerType` (set by the number-key
 * `keydown` AND the `.tower-btn` chips). There is NO parallel `armed` field: every read
 * derives from `gs`, so keyboard/chip arming can never desync (SPEC §8.5, §1.5).
 *
 * The descriptor's `eligible`/`legal`/`canAfford`/`cost` wrap the unchanged
 * `isCellEligible` / `canPlaceAt` / economy checks; `commit` delegates to the shared
 * {@link PlacePort} (the reused place body) — reuse, not rewrite (determinism-neutral).
 */
import { FIREWALL_DEFAULTS } from "../entities/Firewall.ts";
import {
  canPlaceAt,
  isCellEligible,
  type PlacementKind,
} from "../systems/PlacementGuard.ts";
import type { Grid } from "../core/Grid.ts";
import type { Pathfinding } from "../core/Pathfinding.ts";
import type { EconomySystem } from "../systems/EconomySystem.ts";
import type { Element } from "../entities/elements.ts";
import type { TowerConfig } from "../entities/types.ts";
import type { PlaceableDescriptor, PlacePort } from "./placementTypes.ts";

export interface ArmedProviderDeps {
  gs: {
    placementMode: string;
    selectedTowerType: Element | null;
    firewalls: { length: number };
    maxFirewalls: number;
  };
  towerData: Record<string, TowerConfig>;
  grid: Grid;
  pathfinding: Pathfinding;
  economy: Pick<EconomySystem, "canAfford" | "freeFirstTower">;
  placePort: PlacePort;
}

/**
 * Returns `() => PlaceableDescriptor | null` — the live armed placeable, or null when
 * nothing is armed (no firewall mode, no selected tower type). Cheap to call (built on
 * demand at aim/down/up, never per-pixel), so it always reflects the current `gs`.
 */
export function createArmedProvider(
  deps: ArmedProviderDeps,
): () => PlaceableDescriptor | null {
  const { gs, towerData, grid, pathfinding, economy, placePort } = deps;

  const legal = (col: number, row: number, kind: PlacementKind): boolean =>
    canPlaceAt(
      grid,
      pathfinding,
      grid.getEntries(),
      grid.getExits(),
      col,
      row,
      kind,
    );

  const eligible = (col: number, row: number, kind: PlacementKind): boolean => {
    const cell = grid.getCell(col, row);
    return cell ? isCellEligible(cell.type, kind) : false;
  };

  function makeDescriptor(
    kind: PlacementKind,
    config: TowerConfig | undefined,
    cost: number,
    canAfford: () => boolean,
  ): PlaceableDescriptor {
    const desc: PlaceableDescriptor = {
      kind,
      config,
      cost: () => cost,
      canAfford,
      eligible: (col, row) => eligible(col, row, kind),
      legal: (col, row) => legal(col, row, kind),
      commit: (col, row) => placePort.commit(desc, col, row),
    };
    return desc;
  }

  return function getArmed(): PlaceableDescriptor | null {
    if (gs.placementMode === "firewall") {
      const cost = FIREWALL_DEFAULTS.cost;
      // Firewall affordability folds in the maxFirewalls cap (SPEC §1 correction 6).
      return makeDescriptor(
        "firewall",
        undefined,
        cost,
        () => economy.canAfford(cost) && gs.firewalls.length < gs.maxFirewalls,
      );
    }

    const config =
      gs.selectedTowerType != null ? towerData[gs.selectedTowerType] : undefined;
    if (!config) return null;

    const kind: PlacementKind = config.element === "signal" ? "signal" : "tower";
    // Tower/signal affordability folds in the free-first-tower grant (mirrors the legacy
    // mousemove `economy.canAfford(cost) || economy.freeFirstTower`).
    return makeDescriptor(
      kind,
      config,
      config.cost,
      () => economy.canAfford(config.cost) || economy.freeFirstTower,
    );
  };
}
